import { isNorthAmericaCountry } from 'app/modules/fintracStr/formConfig/definitions/common/conditions';
import {
  FINTRAC_STR_ADDRESS_TYPE_CODE,
  FINTRAC_STR_ADDRESS_TYPE_CODE_OPTIONS,
  FINTRAC_STR_CANADA_STATE_CODE_OPTIONS,
  FINTRAC_STR_COUNTRY_CODE,
  FINTRAC_STR_COUNTRY_CODE_OPTIONS,
  FINTRAC_STR_MEXICO_STATE_CODE_OPTIONS,
  FINTRAC_STR_UNITED_STATES_STATE_CODE_OPTIONS,
} from 'app/modules/fintracStr/formConstants';
import { getDependentPaths } from 'app/modules/fintracStr/formUtils';
import {
  FintracFormFieldConditional,
  FintracFormSingleField,
  FintracFormValue,
} from 'app/modules/fintracStr/models';
import { isFieldBlank } from 'app/modules/fintracStr/renderConditions';
import {
  U21FormFieldTypes,
  U21SelectOptionProps,
} from 'app/shared/u21-ui/components';
import { getIn } from 'final-form';
import { isUndefined } from 'lodash';

const isStructuredAddress = (value: FintracFormValue) =>
  value === FINTRAC_STR_ADDRESS_TYPE_CODE.STRUCTURED_ADDRESS.value;
export const fintracStrStructuredAddressFields = (
  basePath: (sectionIndex: number, fieldSectionIndex?: number[]) => string,
  hideIfTrue?: FintracFormFieldConditional,
): FintracFormSingleField[] => [
  [
    {
      type: U21FormFieldTypes.TEXT,
      name: 'buildingNumber',
      label: 'Building number',
      mustExistInPayload: false,
      mustHaveValueIfExist: false,
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          hideIfTrue ? [hideIfTrue] : undefined,
          [`${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`],
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: {
        ...hideIfTrue,
        or: [
          ...(hideIfTrue?.or || []),
          {
            path: (sectionIndex, fieldSectionIndex) =>
              `${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`,
            conditions: (value) =>
              isFieldBlank(value) || !isStructuredAddress(value),
          },
        ],
      },
    },
    {
      type: U21FormFieldTypes.TEXT,
      name: 'unitNumber',
      label: 'Unit number',
      mustExistInPayload: false,
      mustHaveValueIfExist: false,
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          hideIfTrue ? [hideIfTrue] : undefined,
          [`${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`],
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: {
        ...hideIfTrue,
        or: [
          ...(hideIfTrue?.or || []),
          {
            path: (sectionIndex, fieldSectionIndex) =>
              `${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`,
            conditions: (value) =>
              isFieldBlank(value) || !isStructuredAddress(value),
          },
        ],
      },
    },
  ],
  [
    {
      type: U21FormFieldTypes.TEXT,
      name: 'streetAddress',
      label: 'Street name',
      mustExistInPayload: false,
      mustHaveValueIfExist: false,
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          hideIfTrue ? [hideIfTrue] : undefined,
          [`${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`],
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: {
        ...hideIfTrue,
        or: [
          ...(hideIfTrue?.or || []),
          {
            path: (sectionIndex, fieldSectionIndex) =>
              `${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`,
            conditions: (value) =>
              isFieldBlank(value) || !isStructuredAddress(value),
          },
        ],
      },
    },
    {
      type: U21FormFieldTypes.TEXT,
      name: 'city',
      label: 'City',
      mustExistInPayload: false,
      mustHaveValueIfExist: false,
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          hideIfTrue ? [hideIfTrue] : undefined,
          [`${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`],
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: {
        ...hideIfTrue,
        or: [
          ...(hideIfTrue?.or || []),
          {
            path: (sectionIndex, fieldSectionIndex) =>
              `${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`,
            conditions: (value) =>
              isFieldBlank(value) || !isStructuredAddress(value),
          },
        ],
      },
    },
  ],
  [
    {
      type: U21FormFieldTypes.TEXT,
      name: 'district',
      label: 'District',
      mustExistInPayload: false,
      mustHaveValueIfExist: false,
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          hideIfTrue ? [hideIfTrue] : undefined,
          [`${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`],
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: {
        ...hideIfTrue,
        or: [
          ...(hideIfTrue?.or || []),
          {
            path: (sectionIndex, fieldSectionIndex) =>
              `${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`,
            conditions: (value) =>
              isFieldBlank(value) || !isStructuredAddress(value),
          },
        ],
      },
    },
    {
      type: U21FormFieldTypes.TEXT,
      name: 'postalZipCode',
      label: 'Postal/Zip code',
      mustExistInPayload: false,
      mustHaveValueIfExist: false,
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          hideIfTrue ? [hideIfTrue] : undefined,
          [`${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`],
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: {
        ...hideIfTrue,
        or: [
          ...(hideIfTrue?.or || []),
          {
            path: (sectionIndex, fieldSectionIndex) =>
              `${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`,
            conditions: (value) =>
              isFieldBlank(value) || !isStructuredAddress(value),
          },
        ],
      },
    },
  ],
  [
    {
      type: U21FormFieldTypes.SELECT,
      name: 'provinceStateCode',
      label: 'Province/State code',
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          hideIfTrue ? [hideIfTrue] : undefined,
          [
            `${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`,
            `${basePath(sectionIndex, fieldSectionIndex)}.address.countryCode`,
          ],
          sectionIndex,
          fieldSectionIndex,
        ),
      fieldProps: (flattenedValues, _, sectionIndex, fieldSectionIndex) => ({
        options: ((): U21SelectOptionProps<string>[] => {
          if (isUndefined(sectionIndex)) {
            return [];
          }

          const countryCode: string | undefined =
            flattenedValues[
              `${basePath(sectionIndex, fieldSectionIndex)}.address.countryCode`
            ];

          switch (countryCode) {
            case FINTRAC_STR_COUNTRY_CODE.CANADA.value:
              return FINTRAC_STR_CANADA_STATE_CODE_OPTIONS;
            case FINTRAC_STR_COUNTRY_CODE.UNITED_STATES.value:
              return FINTRAC_STR_UNITED_STATES_STATE_CODE_OPTIONS;
            case FINTRAC_STR_COUNTRY_CODE.MEXICO.value:
              return FINTRAC_STR_MEXICO_STATE_CODE_OPTIONS;
            default:
              return [];
          }
        })(),
      }),
      mustExistInPayload: false,
      mustHaveValueIfExist: true,
      hideIfTrue: {
        ...hideIfTrue,
        or: [
          ...(hideIfTrue?.or || []),
          {
            path: (sectionIndex, fieldSectionIndex) =>
              `${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`,
            conditions: (value) =>
              isFieldBlank(value) || !isStructuredAddress(value),
          },
          {
            path: (sectionIndex, fieldSectionIndex) =>
              `${basePath(sectionIndex, fieldSectionIndex)}.address.countryCode`,
            conditions: (value) => !isNorthAmericaCountry(value),
          },
        ],
      },
    },
    {
      type: U21FormFieldTypes.TEXT,
      name: 'provinceStateName',
      label: 'Province/State name',
      mustExistInPayload: false,
      mustHaveValueIfExist: false,
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          hideIfTrue ? [hideIfTrue] : undefined,
          [
            `${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`,
            `${basePath(sectionIndex, fieldSectionIndex)}.address.countryCode`,
          ],
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: {
        ...hideIfTrue,
        or: [
          ...(hideIfTrue?.or || []),
          {
            path: (sectionIndex, fieldSectionIndex) =>
              `${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`,
            conditions: (value) =>
              isFieldBlank(value) || !isStructuredAddress(value),
          },
          {
            path: (sectionIndex, fieldSectionIndex) =>
              `${basePath(sectionIndex, fieldSectionIndex)}.address.countryCode`,
            conditions: (value) => isNorthAmericaCountry(value),
          },
        ],
      },
    },
    {
      type: U21FormFieldTypes.TEXT,
      name: 'subProvinceSubLocality',
      label: 'Sub-province/Sub-locality',
      mustExistInPayload: false,
      mustHaveValueIfExist: false,
      dependentPaths: (sectionIndex, fieldSectionIndex) =>
        getDependentPaths(
          hideIfTrue ? [hideIfTrue] : undefined,
          [`${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`],
          sectionIndex,
          fieldSectionIndex,
        ),
      hideIfTrue: {
        ...hideIfTrue,
        or: [
          ...(hideIfTrue?.or || []),
          {
            path: (sectionIndex, fieldSectionIndex) =>
              `${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`,
            conditions: (value) =>
              isFieldBlank(value) || !isStructuredAddress(value),
          },
        ],
      },
    },
  ],
];

export const fintracStrUnstructuredAddressFields = (
  basePath: (sectionIndex: number, fieldSectionIndex?: number[]) => string,
  hideIfTrue?: FintracFormFieldConditional,
): FintracFormSingleField[] => [
  {
    type: U21FormFieldTypes.TEXT,
    name: 'unstructured',
    label: 'Unstructured address',
    mustExistInPayload: true,
    mustHaveValueIfExist: false,
    dependentPaths: (sectionIndex, fieldSectionIndex) =>
      getDependentPaths(
        hideIfTrue ? [hideIfTrue] : undefined,
        [`${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`],
        sectionIndex,
        fieldSectionIndex,
      ),
    hideIfTrue: {
      ...hideIfTrue,
      or: [
        ...(hideIfTrue?.or || []),
        {
          path: (sectionIndex, fieldSectionIndex) =>
            `${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`,
          conditions: (value) =>
            isFieldBlank(value) || isStructuredAddress(value),
        },
      ],
    },
  },
];

export const fintracStrAddressFields = (
  basePath: (sectionIndex: number, fieldSectionIndex?: number[]) => string,
  hideIfTrue?: FintracFormFieldConditional,
): FintracFormSingleField[] => [
  {
    type: U21FormFieldTypes.SELECT,
    name: 'addressTypeCode',
    label: 'Address type',
    defaultValue: FINTRAC_STR_ADDRESS_TYPE_CODE.STRUCTURED_ADDRESS.value,
    fieldProps: {
      options: FINTRAC_STR_ADDRESS_TYPE_CODE_OPTIONS,
    },
    mustExistInPayload: false,
    mustHaveValueIfExist: true,
    dependentPaths: (sectionIndex, fieldSectionIndex) =>
      getDependentPaths(
        hideIfTrue ? [hideIfTrue] : undefined,
        undefined,
        sectionIndex,
        fieldSectionIndex,
      ),
    hideIfTrue,
  },

  {
    type: 'section',
    repeatable: false,
    label: 'Address',
    name: 'address',
    mustExistInPayload: true,
    mustHaveValueIfExist: false,
    isSectionWrapperHidden: true,
    fields: [
      ...fintracStrStructuredAddressFields(basePath, hideIfTrue),
      ...fintracStrUnstructuredAddressFields(basePath, hideIfTrue),
      {
        type: U21FormFieldTypes.SELECT,
        name: 'countryCode',
        label: 'Country code',
        fieldProps: {
          options: FINTRAC_STR_COUNTRY_CODE_OPTIONS,
        },
        mustExistInPayload: false,
        mustHaveValueIfExist: true,
        dependentPaths: (sectionIndex, fieldSectionIndex) =>
          getDependentPaths(
            hideIfTrue ? [hideIfTrue] : undefined,
            [`${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`],
            sectionIndex,
            fieldSectionIndex,
          ),
        hideIfTrue: {
          ...hideIfTrue,
          or: [
            ...(hideIfTrue?.or || []),
            {
              path: (sectionIndex, fieldSectionIndex) =>
                `${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`,
              conditions: (value) => isFieldBlank(value),
            },
          ],
        },
      },
      {
        type: U21FormFieldTypes.TEXT,
        name: 'typeCode',
        label: 'Type code',
        hideIfTrue,
        disabled: true,
        mustExistInPayload: true,
        mustHaveValueIfExist: true,
        isHidden: true,
        formToPayloadValueConversion: (_, formValues, indexes) => {
          if (indexes.length > 0) {
            const [sectionIndex, ...fieldSectionIndex] = indexes;

            const value = getIn(
              formValues,
              `${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`,
            );

            return {
              typeCode: value,
            };
          }

          return {};
        },
      },
    ],
    dependentPaths: (sectionIndex, fieldSectionIndex) =>
      getDependentPaths(
        hideIfTrue ? [hideIfTrue] : undefined,
        [`${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`],
        sectionIndex,
        fieldSectionIndex,
      ),
    hideIfTrue: {
      ...hideIfTrue,
      or: [
        ...(hideIfTrue?.or || []),
        {
          path: (sectionIndex, fieldSectionIndex) =>
            `${basePath(sectionIndex, fieldSectionIndex)}.addressTypeCode`,
          conditions: (value) => isFieldBlank(value),
        },
      ],
    },
  },
];
